/**
 * WaMessageRow — renders one entry in the message list.
 *
 * Receives all thread-level state as props (no context) so it stays
 * independently testable. Reactive flags (isHovered, isMenuOpen, …) are
 * pre-computed by WaMessageThread before being passed in.
 */

import { Fragment, lazy, Suspense } from 'react';
import {
  Badge,
  Box,
  Button,
  Icon,
  InlineStack,
  Spinner,
  Text,
} from '@/lib/polaris';
import {
  NoteIcon,
  LinkIcon,
  ChevronDownIcon,
  PinFilledIcon,
  PinIcon,
  ChevronRightIcon,
  PlusIcon,
  ReplayIcon,
  InfoIcon,
  ReturnIcon,
  ClipboardIcon,
  SmileyHappyIcon,
  SendIcon,
  StarIcon,
  StarFilledIcon,
  DeleteIcon,
  ImportIcon,
  CheckIcon,
  LocationIcon,
  FileIcon,
} from '@/lib/polaris-icons';
import { DeliveryIcon, WaAudioPlayer, QUICK_REACTIONS } from './WaAudioPlayer';
import { WaMediaSkeleton } from './WaMediaSkeleton';
import { waMediaPreview } from './wa-helpers';
import { WaOrderCard } from './WaOrderCard';
/**
 * 🪤 Cargado con `lazy()`, no de forma estática. Este componente arrastra `react-leaflet` + `leaflet`
 * (~440 KB de fuente) y `WaMessageRow` cuelga del layout del panel: en estático, la librería de mapas
 * entra al ARRANQUE de todo el panel para una burbuja de ubicación que la mayoría de las
 * conversaciones no tiene.
 *
 * Medido (ADR-0012, paso 4): el arranque había subido de 883 a 1.002 KB comprimidos por este camino.
 * El candado `npm run check:arranque` no lo atajó porque leaflet no estaba en su lista; ahora sí.
 */
const WaLocationPreview = lazy(() =>
  import('./WaLocationPreview').then((m) => ({ default: m.WaLocationPreview })),
);
import { WaCoverageCheckCard } from './WaCoverageCheckCard';
import { TwemojiText } from '@/components/TwemojiText';
import { WaFormattedText } from './WaFormattedText';
import { WaPollCard, isPollBody } from './WaPollCard';
import type { WaMessage } from './WaMessageThread';
import { useWaLazyMedia } from '@/hooks/useWaLazyMedia';
import { deriveParticipantAvatarKey } from '@/hooks/useGroupParticipantAvatars';
import { groupParticipantColor, groupParticipantInitials } from './_helpers/messageRowHelpers';
import { WaAvatar } from './WaAvatar';
import { useDensidadHilo } from './waThreadDensity';

// ─── @mention highlighter ───────────────────────────────────────────────────

function renderTextWithMentionsHighlight(body: string, names: string[] | undefined): React.ReactNode[] {
  if (!body) return [];
  if (!names || names.length === 0) return [body];
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const escaped = sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`@(${escaped.join('|')})\\b`, 'gi');
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push(body.slice(last, m.index));
    out.push(
      <span
        key={`mention-${key++}`}
        style={{
          background: 'rgba(37, 211, 102, 0.18)',
          color: '#075E54',
          fontWeight: 600,
          padding: '0 2px',
          borderRadius: 3,
        }}
      >
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push(body.slice(last));
  return out;
}

/**
 * De dónde salió el mensaje, dicho en una palabra.
 *
 * POR QUÉ. El founder vio llegar un mensaje de calidad y no tenía forma de saber si lo escribió una
 * persona, la IA, una campaña o un aviso automático. Los de la IA ya se distinguían; el resto no. Sin
 * eso, revisar un chat obliga a adivinar quién le habló al cliente — y si el mensaje salió mal, no se
 * sabe qué apagar.
 *
 * 🪤 SOLO SE ETIQUETA LO QUE NO ES UNA PERSONA. Un chip «Humano» en cada mensaje del equipo sería ruido
 * en el 95% del hilo: lo normal no necesita anuncio, lo automático sí. Y los mensajes viejos, sin
 * origen registrado, no llevan nada — inventarles una etiqueta afirmaría algo que nadie sabe.
 *
 * 🪤 EL COLOR ES SOBRIO A PROPÓSITO. Compite con la burbuja, la hora y los checks en un espacio de
 * pocos píxeles; un chip llamativo por mensaje volvería el hilo ilegible. Se lee cuando se lo busca.
 */
const ETIQUETA_DE_ORIGEN: Record<string, string> = {
  agente_ia: 'IA',
  campana_calidad: 'Calidad',
  campana_venta: 'Remarketing',
  aviso_entrega: 'Entrega',
  programado: 'Programado',
};

/**
 * ¿Esto es un aviso interno de Smarty, o un mensaje que el cliente recibió?
 *
 * 🚨 `sender_type = 'system'` DICE QUIÉN LO ESCRIBIÓ, NO QUÉ ES. Tomarlo como «aviso interno» hacía
 * que los mensajes de campaña —que el cliente recibió en su teléfono igual que cualquier otro— se
 * dibujaran como una nota del sistema: texto plano, sin negritas, sin chip de origen y sin menú. El
 * founder lo vio así: «no se reconoce el formato negrita de los mensajes de calidad, tampoco están los
 * identificadores».
 *
 * Un aviso es algo que pasó DENTRO de Smarty y el cliente nunca vio. Lo que separa una cosa de la otra
 * no es quién lo generó, sino si salió.
 *
 * 🪤 NO ALCANZA CON MIRAR `wa_message_id`: las llamadas perdidas son avisos y TIENEN uno, porque llegan
 * como evento de WhatsApp. Por eso la condición exige además que sea saliente.
 *
 * 🪤 Y SE INCLUYE `origen` A PROPÓSITO. Una campaña que falló al enviarse no tiene id de WhatsApp; sin
 * esta parte se disfrazaría de nota del sistema y el envío roto desaparecería justo de la vista de
 * quien tiene que notarlo.
 */
export function esAvisoDelSistema(msg: {
  sender_type?: string | null;
  direction?: string | null;
  wa_message_id?: string | null;
  origen?: string | null;
}): boolean {
  if (msg.sender_type !== 'system') return false;
  const salioAlCliente = msg.direction === 'outbound' && (!!msg.wa_message_id || !!msg.origen);
  return !salioAlCliente;
}

function ChipDeOrigen({ origen }: { origen: string | null | undefined }) {
  const texto = origen ? ETIQUETA_DE_ORIGEN[origen] : null;
  if (!texto) return null;
  return (
    <span
      title={`Mensaje enviado por: ${texto}`}
      style={{
        fontSize: 9.5, lineHeight: 1.4, fontWeight: 600, letterSpacing: 0.2,
        padding: '0 4px', borderRadius: 4, flexShrink: 0,
        background: 'var(--p-color-bg-surface-secondary)',
        color: 'var(--p-color-text-subdued)',
        border: '1px solid var(--p-color-border-secondary)',
      }}
    >
      {texto}
    </span>
  );
}

// ─── MsgBadges ──────────────────────────────────────────────────────────────

function MsgBadges({ isStarred, isPinned }: { isStarred: boolean; isPinned: boolean }) {
  if (!isStarred && !isPinned) return null;
  return (
    <>
      {isStarred && (
        <span style={{ color: '#fbbf24', display: 'flex', flexShrink: 0, transform: 'scale(0.6)' }}>
          <Icon source={StarFilledIcon} />
        </span>
      )}
      {isPinned && (
        <span style={{ color: '#8696a0', display: 'flex', flexShrink: 0, transform: 'scale(0.6)' }}>
          <Icon source={PinFilledIcon} />
        </span>
      )}
    </>
  );
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface WaMessageRowProps {
  // Core data
  msg: WaMessage;
  isNew: boolean;
  showDate: boolean;
  /**
   * El mensaje anterior es de otra persona (o es el primero). Decide cuánto aire lleva arriba: los
   * mensajes seguidos del mismo autor van pegados, el cambio de turno se separa.
   */
  cambioDeAutor?: boolean;
  pills: React.ReactNode;

  // Per-message reactive state (pre-computed in parent)
  isHovered: boolean;
  isMenuOpen: boolean;
  menuOpenUp: boolean;
  isReactionPickerOpen: boolean;
  isHighlighted: boolean;
  isRetrying: boolean;
  retryFailed: boolean;
  isSelected: boolean;
  isPinned: boolean;
  selectMode: boolean;

  // Config (stable across all rows)
  contactPhotoUrl?: string | null;
  currentUserAvatarUrl?: string | null;
  mentionableNames?: string[];
  uniqueAgentCount: number;
  /** Map: participantPhone (solo dígitos) → avatarUrl. Usado para
   *  renderizar la foto real del participante de grupo en lugar de
   *  iniciales. Si el phone no está o el url es null, fallback iniciales. */
  groupParticipantAvatars?: Record<string, string | null>;
  /** Skip la columna entera del avatar del participante (ni avatar ni
   *  sangría). Lo usa el BurstContainer en WaMessageThread, que dibuja
   *  un único avatar sticky compartido para todo el burst. */
  omitParticipantAvatarColumn?: boolean;
  /** Map userpart→nombre para resolver @menciones de grupo en el body. */
  groupMentionMap?: Map<string, string>;

  // Refs (stable)
  msgRef: (el: HTMLDivElement | null) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;

  // Data handlers (stable)
  onSendReaction: (msgId: string, emoji: string) => void;
  onRetryMessage?: (msgId: string) => void;
  onForwardMessage?: (msg: WaMessage) => void;
  onDeleteMessage?: (msgId: string) => void;
  onReplyMessage?: (msg: WaMessage) => void;
  onEditMessage?: (msg: WaMessage) => void;
  onShowMessageInfo?: (msg: WaMessage) => void;
  onUnpinMessage?: () => void;
  onStarMessage?: (msgId: string) => void;
  /** Feature coverage: enviar mensaje template al cliente (insertar+enviar). */
  onSendQuickReply?: (text: string) => void;
  /** Para verificar cobertura: el endpoint resuelve tenant desde aquí. */
  conversationId?: string;
  /** Feature 5c-B: abre modal con detalle de items del orderMessage.
   *  Si el pre-fetch ya pobló order_info.items, los pasamos directo para
   *  evitar un round-trip al edge function. */
  onShowOrderDetails?: (params: {
    orderId: string;
    token: string;
    totalAmount1000?: number;
    currency?: string;
    prefetchedItems?: Array<Record<string, unknown>>;
    orderDate: string;
  }) => void;
  resolveAgentName: (id: string) => string;
  retryMediaDownload: (messageId: string) => void;

  // UI callbacks (capture msg.id in parent)
  onHoverStart: () => void;
  onHoverEnd: () => void;
  onMenuOpen: (openUp: boolean) => void;
  onMenuClose: () => void;
  onReactionPickerToggle: () => void;
  onReactionPickerClose: () => void;
  onLightboxOpen: (url: string) => void;
  onPinModalOpen: () => void;
  onReactionDetailOpen: () => void;
  onSelectToggle: () => void;
  onSelectModeStart: () => void;
  onRetryTarget: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function WaMessageRow({
  msg, isNew, showDate, cambioDeAutor = false, pills,
  isHovered, isMenuOpen, menuOpenUp, isReactionPickerOpen,
  isHighlighted, isRetrying, retryFailed, isSelected, isPinned, selectMode,
  contactPhotoUrl, currentUserAvatarUrl, mentionableNames, uniqueAgentCount,
  groupParticipantAvatars,
  groupMentionMap,
  omitParticipantAvatarColumn,
  msgRef, scrollContainerRef, messagesEndRef,
  onSendReaction, onRetryMessage, onForwardMessage, onDeleteMessage,
  onReplyMessage, onEditMessage, onShowMessageInfo, onUnpinMessage, onStarMessage, onShowOrderDetails, onSendQuickReply, conversationId,
  resolveAgentName, retryMediaDownload,
  onHoverStart, onHoverEnd, onMenuOpen, onMenuClose,
  onReactionPickerToggle, onReactionPickerClose,
  onLightboxOpen, onPinModalOpen, onReactionDetailOpen,
  onSelectToggle, onSelectModeStart, onRetryTarget,
}: WaMessageRowProps) {
  // Espacio libre del lado opuesto a la burbuja. Sale del contexto del hilo porque depende del
  // ancho que este tenga: en una ventana acoplada, 60px fijos estrangulaban el texto.
  const { canaleta, saltoMismoAutor, saltoOtroAutor, fuente, fuenteHora } = useDensidadHilo();
  // Aire vertical: mínimo entre mensajes seguidos de la misma persona, mayor cuando cambia el
  // turno. Es lo que deja leer de un vistazo quién dijo qué sin tener que mirar los colores.
  const saltoArriba = cambioDeAutor ? saltoOtroAutor : saltoMismoAutor;
  const isOutbound = msg.direction === 'outbound';
  const isNote = msg.is_internal_note;
  const isSystem = esAvisoDelSistema(msg);
  const isStarred = (msg as WaMessage & { is_starred?: boolean }).is_starred ?? false;
  const isDeleted = !!msg.deleted_at;

  const { resolvedUrl, loading: mediaFetching, failed: mediaFetchFailed, observeRef } = useWaLazyMedia({
    messageId: msg.id,
    mediaUrl: msg.media_url,
    mediaStatus: msg.media_status ?? null,
    msgType: msg.msg_type,
    createdAt: msg.created_at,
  });
  // effectiveUrl: prioridad URL full > thumbnail base64 inline. Cuando llega
  // por primera vez el INSERT del webhook, media_url es null pero
  // media_thumbnail tiene el data URI del preview de Baileys → renderiza al
  // toque. Cuando termina la descarga full, realtime UPDATE setea media_url
  // y aparece la imagen nítida. Patrón WhatsApp Web: nunca spinner vacío.
  const thumbnail = (msg as WaMessage & { media_thumbnail?: string | null }).media_thumbnail ?? null;
  const fullUrl = resolvedUrl ?? msg.media_url;
  const effectiveUrl = fullUrl ?? thumbnail;
  const isShowingThumbnail = !fullUrl && !!thumbnail;

  return (
    <Fragment>
      {pills}
      <div
        data-msg-id={msg.id}
        className={isNew ? 'wa-msg-new' : undefined}
        ref={(el) => {
          (observeRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          if (typeof msgRef === 'function') (msgRef as (el: HTMLDivElement | null) => void)(el);
          else if (msgRef) (msgRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }}
        onClick={selectMode ? onSelectToggle : undefined}
        style={{
          ...(isHighlighted ? {
            background: 'rgba(37, 211, 102, 0.15)',
            transition: 'background 0.5s ease',
          } : {}),
          ...(isSelected ? { background: 'rgba(0, 168, 132, 0.12)' } : {}),
          borderRadius: 8,
          display: selectMode ? 'flex' : undefined,
          alignItems: selectMode ? 'center' : undefined,
          gap: selectMode ? 8 : undefined,
          cursor: selectMode ? 'pointer' : undefined,
          padding: selectMode ? '0 8px' : undefined,
        }}
      >
        {/* Checkbox en modo selección */}
        {selectMode && (
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            border: isSelected ? '2px solid #00a884' : '2px solid #8696a0',
            background: isSelected ? '#00a884' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {isSelected && (
              <span style={{ color: '#fff', display: 'flex', transform: 'scale(0.7)' }}>
                <Icon source={CheckIcon} />
              </span>
            )}
          </div>
        )}

        {/* Date separator */}
        {showDate ? (
          <Box paddingBlockStart="400" paddingBlockEnd="300">
            <InlineStack align="center">
              <span style={{
                background: 'var(--p-color-bg-surface)',
                border: '1px solid var(--p-color-border-subdued)',
                borderRadius: 8, padding: '4px 14px',
                fontSize: 12, color: 'var(--p-color-text-subdued)',
              }}>
                {new Date(msg.created_at).toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>
            </InlineStack>
          </Box>
        ) : null}

        {/* System message */}
        {isSystem ? (
          <Box paddingBlock="200">
            <InlineStack align="center">
              <span style={{
                background: 'var(--p-color-bg-surface)', borderRadius: 8,
                padding: '4px 12px', fontSize: 12, color: 'var(--p-color-text-subdued)',
              }}>
                {msg.body}
              </span>
            </InlineStack>
          </Box>
        ) : msg.msg_type === 'sticker' && effectiveUrl ? (
          /* Sticker: sin burbuja, imagen pequeña */
          <div style={{
            display: 'flex',
            justifyContent: isOutbound ? 'flex-end' : 'flex-start',
            marginTop: saltoArriba,
            marginBottom: saltoMismoAutor,
            paddingLeft: isOutbound ? canaleta : 0,
            paddingRight: isOutbound ? 0 : canaleta,
          }}>
            <div style={{ textAlign: isOutbound ? 'right' : 'left' }}>
              <img
                src={effectiveUrl}
                alt="sticker"
                style={{ width: 160, height: 160, objectFit: 'contain', display: 'block' }}
                loading="eager"
                onLoad={() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginTop: 2, alignItems: 'center' }}>
                <MsgBadges isStarred={isStarred} isPinned={isPinned} />
                <ChipDeOrigen origen={msg.origen} />
                <span style={{ fontSize: 11, color: 'var(--p-color-text-subdued)' }}>
                  {new Date(msg.created_at).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                </span>
                {isOutbound ? (
                  <DeliveryIcon
                    status={msg.delivery_status}
                    onRetry={onRetryMessage ? onRetryTarget : undefined}
                  />
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: isOutbound ? 'flex-end' : 'flex-start',
              alignItems: 'flex-end',
              gap: omitParticipantAvatarColumn ? 0 : 8,
              // Pegado al mensaje anterior si es de la misma persona; con aire si cambió el turno.
              marginTop: saltoArriba,
              marginBottom: msg.reactions?.length > 0 ? 18 : saltoMismoAutor,
              paddingLeft: isOutbound ? canaleta : 0,
              // Cuando el row vive dentro de un BurstContainer (omitColumn),
              // el padre maneja la sangría derecha — acá NO la duplicamos.
              paddingRight: isOutbound ? 0 : (omitParticipantAvatarColumn ? 0 : canaleta),
            }}
            onMouseEnter={onHoverStart}
            onMouseLeave={onHoverEnd}
          >
            {/* Avatar del participante en inbound de grupos. Se oculta
                con omitParticipantAvatarColumn cuando vivimos dentro de
                un BurstContainer (el padre dibuja un avatar sticky único). */}
            {!isOutbound && !isNote && msg.group_participant_name && !omitParticipantAvatarColumn ? (() => {
              const cacheKey = deriveParticipantAvatarKey(msg.group_participant_phone, msg.group_participant_jid);
              const avatarUrl = cacheKey ? groupParticipantAvatars?.[cacheKey] : null;
              return (
                <WaAvatar
                  size={32}
                  src={avatarUrl}
                  initials={groupParticipantInitials(msg.group_participant_name)}
                  bg={groupParticipantColor(cacheKey ?? msg.group_participant_name)}
                  fontSize={13}
                  alt={msg.group_participant_name}
                />
              );
            })() : null}
            <div style={{ position: 'relative', maxWidth: '100%' }}>
              {/* Reaction emoji button — outside bubble */}
              {(isHovered || isReactionPickerOpen) && !isNote && !isSystem && !isDeleted && msg.wa_message_id ? (
                <button
                  type="button"
                  onClick={onReactionPickerToggle}
                  style={{
                    position: 'absolute',
                    top: '50%', transform: 'translateY(-50%)',
                    [isOutbound ? 'left' : 'right']: -36,
                    width: 30, height: 30, borderRadius: '50%',
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, padding: 0, opacity: 0.6,
                    transition: 'opacity 0.15s', zIndex: 10,
                  }}
                  onMouseEnter={(ev) => { ev.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={(ev) => { ev.currentTarget.style.opacity = '0.6'; }}
                  title="Reaccionar"
                >
                  <Icon source={SmileyHappyIcon} tone="subdued" />
                </button>
              ) : null}

              {/* Dropdown arrow — inside bubble */}
              {(isHovered || isMenuOpen) && !isNote && !isSystem && !isDeleted ? (
                <button
                  type="button"
                  onClick={(e) => {
                    if (isMenuOpen) {
                      onMenuClose();
                    } else {
                      const btn = e.currentTarget;
                      const scrollContainer = scrollContainerRef.current;
                      if (scrollContainer) {
                        const btnRect = btn.getBoundingClientRect();
                        const containerRect = scrollContainer.getBoundingClientRect();
                        onMenuOpen(containerRect.bottom - btnRect.bottom < 320);
                      } else {
                        onMenuOpen(false);
                      }
                      onReactionPickerClose();
                    }
                  }}
                  style={{
                    position: 'absolute', top: 2, right: 2,
                    width: 22, height: 22, border: 'none',
                    background: isOutbound ? 'rgba(220,248,198,0.85)' : 'rgba(255,255,255,0.85)',
                    borderRadius: 4, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0, opacity: 0.7, transition: 'opacity 0.15s', zIndex: 10,
                  }}
                  onMouseEnter={(ev) => { ev.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={(ev) => { ev.currentTarget.style.opacity = '0.7'; }}
                  title="Más opciones"
                >
                  <Icon source={ChevronDownIcon} tone="subdued" />
                </button>
              ) : null}

              {/* Quick reaction picker */}
              {isReactionPickerOpen ? (
                <div style={{
                  position: 'absolute', top: -44,
                  [isOutbound ? 'right' : 'left']: 0,
                  zIndex: 20, display: 'flex', gap: 2,
                  background: 'var(--p-color-bg-surface)',
                  border: '1px solid var(--p-color-border-subdued)',
                  borderRadius: 24, padding: '6px 8px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                }}>
                  {QUICK_REACTIONS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => {
                        const alreadyReacted = msg.reactions?.some((r) => r.from_me && r.emoji === e);
                        onSendReaction(msg.id, alreadyReacted ? '' : e);
                        onReactionPickerClose();
                      }}
                      style={{
                        width: 34, height: 34, borderRadius: '50%',
                        border: 'none', background: 'transparent',
                        cursor: 'pointer', fontSize: 20,
                        transition: 'transform 0.15s',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      onMouseEnter={(ev) => { ev.currentTarget.style.transform = 'scale(1.3)'; ev.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; }}
                      onMouseLeave={(ev) => { ev.currentTarget.style.transform = 'scale(1)'; ev.currentTarget.style.background = 'transparent'; }}
                      title={msg.reactions?.some((r) => r.from_me && r.emoji === e) ? 'Quitar reacción' : `Reaccionar con ${e}`}
                    >
                      <TwemojiText size={22}>{e}</TwemojiText>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={onReactionPickerClose}
                    style={{
                      width: 34, height: 34, borderRadius: '50%',
                      border: 'none', background: 'transparent',
                      cursor: 'pointer', fontSize: 18,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#3b4a54',
                    }}
                    onMouseEnter={(ev) => { ev.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; }}
                    onMouseLeave={(ev) => { ev.currentTarget.style.background = 'transparent'; }}
                    title="Más emojis"
                  >
                    <Icon source={PlusIcon} tone="subdued" />
                  </button>
                </div>
              ) : null}

              {/* Context menu dropdown */}
              {isMenuOpen ? (
                <div style={{
                  position: 'absolute',
                  ...(menuOpenUp ? { bottom: 30 } : { top: 30 }),
                  [isOutbound ? 'right' : 'left']: 0,
                  zIndex: 25,
                  background: 'var(--p-color-bg-surface)',
                  borderRadius: 10,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                  padding: '6px 0', minWidth: 200, overflow: 'hidden',
                }}>
                  {[
                    { label: 'Info. del mensaje', iconSrc: InfoIcon, action: () => { onShowMessageInfo?.(msg); onMenuClose(); } },
                    ...(onReplyMessage ? [{ label: 'Responder', iconSrc: ReturnIcon, action: () => { onReplyMessage(msg); onMenuClose(); } }] : []),
                    ...(msg.body ? [{ label: 'Copiar', iconSrc: ClipboardIcon, action: () => { void navigator.clipboard.writeText(msg.body ?? ''); onMenuClose(); } }] : []),
                    // Editar: solo outbound + text + dentro de 15 min (WhatsApp limit).
                    // Si pasaron >15 min, no mostrar (Baileys lo rechazaría).
                    ...(onEditMessage && msg.direction === 'outbound' && msg.msg_type === 'text' && msg.body
                      && (Date.now() - new Date(msg.created_at).getTime() < 15 * 60 * 1000)
                      ? [{ label: 'Editar', iconSrc: ClipboardIcon, action: () => { onEditMessage(msg); onMenuClose(); } }] : []),
                    ...(msg.wa_message_id ? [{ label: 'Reaccionar', iconSrc: SmileyHappyIcon, action: () => { onReactionPickerToggle(); } }] : []),
                    ...(onForwardMessage ? [{ label: 'Reenviar', iconSrc: SendIcon, action: () => { onSelectModeStart(); } }] : []),
                    isPinned
                      ? { label: 'Desfijar', iconSrc: PinIcon, action: () => { onUnpinMessage?.(); onMenuClose(); } }
                      : { label: 'Fijar', iconSrc: PinFilledIcon, action: () => { onPinModalOpen(); onMenuClose(); } },
                    { label: isStarred ? 'Quitar destacado' : 'Destacar', iconSrc: isStarred ? StarFilledIcon : StarIcon, action: () => { onStarMessage?.(msg.id); onMenuClose(); } },
                    ...(onDeleteMessage && msg.direction === 'outbound' ? [{ label: 'Eliminar para todos', iconSrc: DeleteIcon, action: () => { onDeleteMessage(msg.id); onMenuClose(); }, danger: true }] : []),
                  ].map((item, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={item.action}
                      style={{
                        width: '100%', border: 'none',
                        background: 'transparent', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 20px', fontSize: 14, textAlign: 'left',
                        color: (item as { danger?: boolean }).danger ? '#e53935' : '#3b4a54',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(ev) => { ev.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; }}
                      onMouseLeave={(ev) => { ev.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ width: 20, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                        <Icon source={item.iconSrc} tone={(item as { danger?: boolean }).danger ? 'critical' : 'subdued'} />
                      </div>
                      {item.label}
                    </button>
                  ))}
                </div>
              ) : null}

              {/* Bubble */}
              <div style={{
                maxWidth: !isDeleted && (((effectiveUrl || mediaFetching) && (msg.msg_type === 'image' || msg.msg_type === 'video')) || msg.msg_type === 'location') ? 290 : '100%',
                padding: isDeleted
                  ? '6px 10px 6px 10px'
                  : isNote ? '8px 12px'
                  : ((effectiveUrl || mediaFetching) && (msg.msg_type === 'image' || msg.msg_type === 'video')) || msg.msg_type === 'location'
                    ? '0'
                    : effectiveUrl && msg.msg_type === 'audio'
                      ? '5px 8px 3px 8px'
                      : '6px 10px 6px 10px',
                borderRadius: isOutbound ? '10px 10px 2px 10px' : '10px 10px 10px 2px',
                overflow: 'hidden',
                background: isNote ? '#FFF9C4' : isOutbound ? '#DCF8C6' : 'var(--p-color-bg-surface)',
                border: isNote
                  ? '1px solid #FBC02D'
                  : !isDeleted && (((effectiveUrl || mediaFetching) && ['image', 'video', 'audio'].includes(msg.msg_type)) || msg.msg_type === 'location')
                    ? 'none'
                    : isOutbound ? 'none' : '1px solid var(--p-color-border-subdued)',
                boxShadow: '0 1px 1px rgba(0,0,0,0.06)',
                position: 'relative',
              }}>
                {isNote ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                    <Icon source={NoteIcon} tone="subdued" />
                    <Text as="span" variant="bodySm" fontWeight="semibold" tone="subdued">Nota interna</Text>
                  </div>
                ) : null}

                {msg.sender_type === 'ai' && !isNote ? (
                  <div style={{ marginBottom: 2 }}>
                    <Badge tone="info" size="small">IA</Badge>
                  </div>
                ) : null}

                {/* Agent name header (Chatwoot-style, solo si hay 2+ agentes) */}
                {isOutbound && !isNote && msg.sender_type === 'agent' && msg.sender_id && uniqueAgentCount >= 2 ? (
                  <div style={{
                    fontSize: 12, fontWeight: 600, color: '#06cf9c',
                    marginBottom: 2, whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280,
                  }}>
                    {resolveAgentName(msg.sender_id)}
                  </div>
                ) : null}

                {/* Group participant header (estilo WhatsApp Web): nombre +
                    teléfono del que escribió, en color hashed estable por phone.
                    Solo se renderiza para inbound de grupos.
                    Para bubbles edge-to-edge (image/video/location con
                    padding 0), el nombre necesita su propio padding interno
                    sino queda apretado contra el borde de la imagen. */}
                {!isOutbound && !isNote && msg.group_participant_name ? (() => {
                  const isEdgeMediaBubble = !isDeleted && (
                    ((effectiveUrl || mediaFetching) && (msg.msg_type === 'image' || msg.msg_type === 'video'))
                    || msg.msg_type === 'location'
                  );
                  return (
                  <div style={{
                    fontSize: 13, fontWeight: 600,
                    color: groupParticipantColor(msg.group_participant_phone ?? msg.group_participant_name),
                    whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280,
                    // Edge media: añadir padding al nombre para que no
                    // quede pegado a las esquinas de la imagen + dejar
                    // un respiro vertical hasta la foto.
                    padding: isEdgeMediaBubble ? '8px 12px 6px' : 0,
                    marginBottom: isEdgeMediaBubble ? 0 : 2,
                  }}>
                    {msg.group_participant_name}
                    {msg.group_participant_phone ? (
                      <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 6, fontSize: 12 }}>
                        {msg.group_participant_phone}
                      </span>
                    ) : null}
                  </div>
                  );
                })() : null}

                {/* Anuncio de origen (click-to-WhatsApp de Meta). Se pinta arriba
                    del contenido, igual que la tarjeta que ve el cliente en su
                    celular — así el agente sabe QUÉ promo vio antes de escribir. */}
                {msg.ad_referral ? (() => {
                  const ad = msg.ad_referral!;
                  const adTitle = ad.title || 'Anuncio de Facebook/Instagram';
                  const card = (
                    <div
                      style={{
                        display: 'flex', alignItems: 'stretch', gap: 8,
                        margin: '0 0 4px 0', borderRadius: 8, overflow: 'hidden',
                        background: 'rgba(0,0,0,0.05)',
                        borderLeft: '4px solid #1877F2',   // azul Meta
                      }}
                    >
                      {ad.thumbnail ? (
                        <div style={{ position: 'relative', flexShrink: 0, width: 52, height: 52 }}>
                          <img
                            src={ad.thumbnail}
                            alt=""
                            style={{ width: 52, height: 52, objectFit: 'cover', display: 'block' }}
                          />
                          {/* Un anuncio de VIDEO llega como miniatura (still): WhatsApp
                              no manda el video. El ▶ deja claro que era un video. */}
                          {ad.mediaType === 'video' ? (
                            <span style={{
                              position: 'absolute', inset: 0, display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              background: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: 16,
                            }}>▶</span>
                          ) : null}
                        </div>
                      ) : null}
                      <div style={{ minWidth: 0, flex: 1, padding: '6px 8px 6px 0' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#1877F2', marginBottom: 2 }}>
                          📣 Vino de un anuncio{ad.sourceType === 'ig' ? ' de Instagram' : ad.sourceType === 'fb' ? ' de Facebook' : ''}
                        </div>
                        <div style={{
                          fontSize: 12.5, fontWeight: 600, color: 'var(--p-color-text)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {adTitle}
                        </div>
                        {ad.body ? (
                          <div style={{
                            fontSize: 11.5, color: 'var(--p-color-text-subdued)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {ad.body}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                  // Con link al anuncio, la tarjeta es clickeable.
                  return ad.sourceUrl
                    ? <a href={ad.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>{card}</a>
                    : card;
                })() : null}

                {/* Quoted message */}
                {msg.quoted_message ? (
                  <div
                    style={{
                      display: 'flex', alignItems: 'stretch',
                      margin: '0 0 4px 0', borderRadius: 8,
                      background: isOutbound ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.04)',
                      overflow: 'hidden', cursor: 'pointer', minHeight: 42,
                    }}
                    onClick={() => {
                      const target = document.querySelector(`[data-msg-id="${msg.quoted_message!.id}"]`);
                      if (target) {
                        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        (target as HTMLElement).style.background = 'rgba(0, 168, 132, 0.15)';
                        setTimeout(() => { (target as HTMLElement).style.background = ''; }, 1500);
                      }
                    }}
                  >
                    <div style={{
                      width: 4, flexShrink: 0,
                      background: msg.quoted_message.direction === 'inbound' ? '#06cf9c' : '#53bdeb',
                      borderRadius: '8px 0 0 8px',
                    }} />
                    <div style={{ flex: 1, padding: '4px 8px', minWidth: 0 }}>
                      <div style={{
                        fontSize: 12, fontWeight: 600,
                        color: msg.quoted_message.direction === 'inbound' ? '#06cf9c' : '#53bdeb',
                        marginBottom: 1,
                      }}>
                        {msg.quoted_message.direction === 'inbound' ? 'Contacto' : 'Tú'}
                      </div>
                      <div style={{ fontSize: 13, color: '#667781', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {waMediaPreview(msg.quoted_message.msg_type) || (msg.quoted_message.body?.slice(0, 100) ?? '')}
                      </div>
                    </div>
                    {msg.quoted_message.media_url && msg.quoted_message.msg_type === 'image' ? (
                      <img src={msg.quoted_message.media_url} alt="" style={{
                        width: 48, height: 48, objectFit: 'cover',
                        borderRadius: '0 8px 8px 0', flexShrink: 0,
                      }} />
                    ) : null}
                  </div>
                ) : null}

                {/* Revoke "Eliminar para todos": reemplaza todo el contenido
                    type-specific con un placeholder estilo WhatsApp Web.
                    Mantiene quoted_message y timestamp; oculta media/text. */}
                {isDeleted ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontStyle: 'italic', color: 'var(--p-color-text-subdued)',
                  }}>
                    <span style={{ display: 'flex', transform: 'scale(0.85)' }}>
                      <Icon source={DeleteIcon} tone="subdued" />
                    </span>
                    <span style={{ fontSize: 13 }}>
                      {isOutbound ? 'Eliminaste este mensaje' : 'Este mensaje fue eliminado'}
                    </span>
                  </div>
                ) : msg.msg_type === 'order' && msg.order_info ? (
                  <WaOrderCard
                    order={msg.order_info}
                    thumbnail={msg.media_thumbnail ?? null}
                    onViewDetails={
                      onShowOrderDetails && msg.order_info.orderId && msg.order_info.token
                        ? () => {
                            const prefetched = msg.order_info?.items as
                              | Array<Record<string, unknown>>
                              | undefined;
                            onShowOrderDetails({
                              orderId: msg.order_info!.orderId!,
                              token: msg.order_info!.token!,
                              totalAmount1000: msg.order_info?.totalAmount1000,
                              currency: msg.order_info?.currency,
                              prefetchedItems: prefetched,
                              orderDate: msg.created_at,
                            });
                          }
                        : undefined
                    }
                  />
                ) : (msg.msg_type === 'audio' || msg.msg_type === 'voice') ? (
                  <>
                    <WaAudioPlayer
                      src={effectiveUrl ?? ''}
                      loadingState={
                        effectiveUrl ? 'loaded'
                        : mediaFetching || isRetrying ? 'downloading'
                        : (mediaFetchFailed || retryFailed) ? 'failed'
                        : 'pending'
                      }
                      onDownloadRetry={msg.wa_message_id ? () => retryMediaDownload(msg.id) : undefined}
                      isOutbound={isOutbound}
                      timestamp={new Date(msg.created_at).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                      deliveryStatus={msg.delivery_status}
                      isNote={isNote}
                      avatarUrl={isOutbound ? currentUserAvatarUrl : contactPhotoUrl}
                      isStarred={isStarred}
                      isPinned={isPinned}
                      knownDuration={msg.audio_duration}
                      onRetry={onRetryMessage ? onRetryTarget : undefined}
                    />
                    {msg.transcription ? (
                      <div style={{
                        margin: '6px 8px 0', padding: '6px 10px',
                        fontSize: 12, fontStyle: 'italic',
                        color: 'var(--p-color-text-subdued)',
                        background: 'var(--p-color-bg-surface-tertiary)',
                        borderRadius: 6, borderLeft: '3px solid var(--p-color-border-info)',
                        maxWidth: 320,
                      }}>
                        <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.7, marginRight: 4 }}>TRANSCRIPCIÓN</span>
                        <span>{msg.transcription}</span>
                      </div>
                    ) : msg.transcription_status === 'processing' ? (
                      <div style={{ margin: '4px 8px 0', fontSize: 11, fontStyle: 'italic', color: 'var(--p-color-text-subdued)' }}>
                        Transcribiendo audio…
                      </div>
                    ) : msg.transcription_status === 'failed' ? (
                      <div style={{ margin: '4px 8px 0', fontSize: 11, color: 'var(--p-color-text-critical)' }}>
                        Transcripción no disponible
                      </div>
                    ) : null}
                  </>
                ) : effectiveUrl ? (
                  <div style={{ marginBottom: ['image', 'video', 'audio'].includes(msg.msg_type) ? 0 : 4 }}>
                    {msg.msg_type === 'image' ? (
                      // Lienzo máximo 290×340. El contenedor toma el ancho de la
                      // burbuja (también 290 cuando hay media) — así la imagen y
                      // el caption comparten exactamente el mismo ancho, sin
                      // espacios. `maxWidth: 100%` + `width/height: auto` mantiene
                      // aspect ratio natural; verticales quedan centradas.
                      <div
                        style={{
                          background: 'var(--p-color-bg-surface-secondary)',
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden',
                          borderRadius: 'inherit',
                          lineHeight: 0,
                          textAlign: 'center',
                        }}
                        onClick={() => onLightboxOpen(effectiveUrl!)}
                      >
                        <img
                          src={effectiveUrl}
                          alt=""
                          style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            maxHeight: 340,
                            width: 'auto',
                            height: 'auto',
                            borderRadius: 'inherit',
                            // Blur sutil mientras se muestra el thumbnail
                            // base64 (preview de Baileys) — feedback visual
                            // de que el media full aún está descargando.
                            // Cuando llega la URL full, blur se elimina con
                            // transición suave.
                            filter: isShowingThumbnail ? 'blur(6px)' : 'none',
                            transition: 'filter 0.3s ease-out',
                          }}
                          loading="eager"
                          onLoad={() => { messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }); }}
                        />
                        {/* Overlay con timestamp solo cuando NO hay caption.
                            Patrón WhatsApp Web: con caption, el timestamp va
                            debajo del texto (más legible y sin pisar la imagen).
                            En imagen/video el caption suele venir en
                            `media_caption` (no en `body`) — chequeamos ambos
                            para no dejar el timestamp pisando la foto cuando
                            hay texto debajo. */}
                        {!msg.body && !msg.media_caption ? (
                          <div style={{
                            position: 'absolute', bottom: 0, right: 0, left: 0,
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.45))',
                            padding: '16px 8px 5px',
                            display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4,
                            borderRadius: '0 0 inherit inherit',
                            lineHeight: 1,
                          }}>
                            <MsgBadges isStarred={isStarred} isPinned={isPinned} />
                            <ChipDeOrigen origen={msg.origen} />
                            <span style={{ fontSize: 11, color: '#ffffffdd', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                              {new Date(msg.created_at).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {isOutbound && !isNote ? (
                              <DeliveryIcon status={msg.delivery_status} light onRetry={onRetryMessage ? onRetryTarget : undefined} />
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : msg.msg_type === 'video' ? (
                      // Mismo patrón que imagen (290×340). Container llena el
                      // ancho de la burbuja para que el caption no la estire.
                      <div style={{
                        background: 'var(--p-color-bg-surface-secondary)',
                        position: 'relative',
                        lineHeight: 0,
                        textAlign: 'center',
                      }}>
                        <video
                          controls
                          src={effectiveUrl!}
                          style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            maxHeight: 340,
                            width: 'auto',
                            height: 'auto',
                            borderRadius: 'inherit',
                          }}
                        />
                      </div>
                    ) : msg.msg_type === 'document' ? (
                      <a href={effectiveUrl!} target="_blank" rel="noopener noreferrer" style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
                        background: 'var(--p-color-bg-surface-secondary)', textDecoration: 'none', color: 'inherit',
                        border: '1px solid var(--p-color-border-subdued)', maxWidth: 280,
                      }}>
                        <span style={{ flexShrink: 0, color: 'var(--p-color-icon-subdued)' }}><Icon source={FileIcon} /></span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          {/* Solo el NOMBRE del archivo: el caption se muestra como
                              texto del mensaje (abajo), no acá — si no, con filename
                              presente el caption quedaba inaccesible. */}
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {msg.media_filename || 'Documento'}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--p-color-text-subdued)' }}>Documento</div>
                        </div>
                        <span style={{ flexShrink: 0, color: 'var(--p-color-text-subdued)' }}><Icon source={ImportIcon} /></span>
                      </a>
                    ) : (
                      <a href={effectiveUrl!} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon source={LinkIcon} tone="subdued" />
                        Archivo
                      </a>
                    )}
                  </div>
                ) : msg.media_status === 'pending' ? (
                  /* Media en descarga: usamos placeholders TYPE-SPECIFIC
                     (estilo WhatsApp Web/iOS): cada tipo tiene su propio
                     formato visual. Audio = waveform skeleton + mic, image
                     = blurred preview, video = play button overlay, etc.
                     NO spinner genérico para todo. */
                  <WaMediaSkeleton
                    msgType={msg.msg_type}
                    isOutbound={isOutbound}
                    duration={msg.audio_duration}
                    filename={msg.media_filename}
                    thumbnail={(msg as WaMessage & { media_thumbnail?: string | null }).media_thumbnail ?? null}
                    isFailed={mediaFetchFailed}
                    hasRetry={!!msg.wa_message_id}
                    onRetry={() => retryMediaDownload(msg.id)}
                  />
                ) : msg.msg_type === 'location' ? (
                  /* Wrapper width 290: DEBE coincidir con el maxWidth del
                     bubble (línea ~568). Si el wrapper es más ancho que el
                     bubble, `overflow: hidden` del bubble recorta el padding
                     derecho del card → contenido visual asimétrico
                     ("alineado a la derecha"). 290 = bubble maxWidth para
                     location/image/video, alinea card y caption del preview
                     a los mismos 12px de margen interno. */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 290 }}>
                    <Suspense fallback={<div style={{ height: 160, borderRadius: 8, background: 'var(--p-color-bg-surface-secondary)' }} />}>
                      <WaLocationPreview
                        body={msg.body ?? ''}
                        caption={msg.media_caption ?? 'Ubicación compartida'}
                      />
                    </Suspense>
                    {conversationId && msg.direction === 'inbound' ? (
                      <WaCoverageCheckCard
                        body={msg.body ?? ''}
                        conversationId={conversationId}
                        onSendQuickReply={onSendQuickReply}
                      />
                    ) : null}
                  </div>
                ) : msg.msg_type !== 'text' && !msg.body && msg.media_status !== 'pending' ? (() => {
                  // Estado terminal: media_status NO es 'pending' y no hay
                  // body. Puede ser status='failed', 'expired', NULL (legacy)
                  // o sin status pero sin URL. Render type-specific con
                  // estado loading o failed según corresponda.
                  const ageSec = (Date.now() - new Date(msg.created_at).getTime()) / 1000;
                  const isInitialLoading = ageSec < 30 && !retryFailed;
                  return (
                    <WaMediaSkeleton
                      msgType={msg.msg_type}
                      isOutbound={isOutbound}
                      duration={msg.audio_duration}
                      filename={msg.media_filename}
                      thumbnail={(msg as WaMessage & { media_thumbnail?: string | null }).media_thumbnail ?? null}
                      isFailed={!isInitialLoading && !isRetrying}
                      hasRetry={!!msg.wa_message_id}
                      onRetry={() => retryMediaDownload(msg.id)}
                    />
                  );
                })() : null}

                {/* Text + timestamp */}
                {(() => {
                  const isImageOrVideo = effectiveUrl && (msg.msg_type === 'image' || msg.msg_type === 'video');
                  // WaAudioPlayer renderiza SU PROPIO timestamp internamente,
                  // siempre — sin importar si la media está descargada o no.
                  // Por eso la condición NO depende de effectiveUrl: aplica
                  // a los 4 estados (pending/downloading/failed/loaded).
                  // Sin esto, el timestamp se duplicaba: uno dentro del audio
                  // player + otro abajo del mensaje.
                  const isAudio = msg.msg_type === 'audio' || msg.msg_type === 'voice';
                  const hasVisualMedia = !isDeleted && (isImageOrVideo || msg.msg_type === 'location');
                  // Caption unificado: WhatsApp guarda el texto de imagen/video
                  // en `media_caption` (no en `body`). Tratar ambos como caption
                  // permite que el footer (texto + hora + check) se renderice
                  // con el mismo layout que un mensaje de texto puro —
                  // mismo color, mismas mentions, mismos links clickeables,
                  // sin overlay sobre la imagen.
                  // Los documentos también muestran su caption: antes se excluían
                  // y, como la tarjeta prioriza media_filename, el texto que mandó
                  // el cliente ("esta es la boleta del pedido 1234") no se veía nunca.
                  const captionText = (msg.body && msg.body.trim().length > 0)
                    ? msg.body
                    : (msg.media_caption ?? '');
                  const hasCaption = !isDeleted && !!captionText && msg.msg_type !== 'location';
                  // Mensaje eliminado: skip audio/visual-only short-circuits
                  // y renderiza solo el timestamp en el bloque común.
                  if (!isDeleted && isAudio) return null;
                  if (!isDeleted && isImageOrVideo && !hasCaption) return null;
                  /*
                   * El tamaño del texto sale de la densidad, no de un número acá: en la ventana
                   * acoplada baja un punto porque el renglón es más corto. La nota interna hereda
                   * del contenedor; el texto formateado lo recibe por `style`.
                   */
                  return (
                    <div style={{ fontSize: fuente, ...(hasVisualMedia ? { padding: '2px 8px 4px' } : {}) }}>
                      {hasCaption ? (
                        isPollBody(captionText)
                          ? <WaPollCard body={captionText} />
                          : isNote
                            ? <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderTextWithMentionsHighlight(captionText, mentionableNames)}</span>
                            : <WaFormattedText text={captionText} mentionMap={groupMentionMap} style={{ fontSize: fuente }} />
                      ) : null}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginTop: 2, alignItems: 'center' }}>
                        {!isDeleted ? <MsgBadges isStarred={isStarred} isPinned={isPinned} /> : null}
                        {!isDeleted && (msg as WaMessage & { is_edited?: boolean }).is_edited ? (
                          <span style={{ fontSize: fuenteHora, color: 'var(--p-color-text-subdued)', fontStyle: 'italic' }}>
                            editado
                          </span>
                        ) : null}
                        <ChipDeOrigen origen={msg.origen} />
                        <span style={{ fontSize: fuenteHora, color: 'var(--p-color-text-subdued)' }}>
                          {new Date(msg.created_at).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isOutbound && !isNote ? (
                          <DeliveryIcon status={msg.delivery_status} onRetry={onRetryMessage ? onRetryTarget : undefined} />
                        ) : null}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Reactions display */}
              {!isDeleted && msg.reactions?.length > 0 ? (
                <div style={{
                  display: 'flex',
                  justifyContent: isOutbound ? 'flex-end' : 'flex-start',
                  marginTop: -10, marginBottom: 4,
                  paddingLeft: isOutbound ? 0 : 4,
                  paddingRight: isOutbound ? 4 : 0,
                  position: 'relative', zIndex: 3,
                }}>
                  <button
                    type="button"
                    onClick={onReactionDetailOpen}
                    style={{
                      display: 'flex', gap: 2,
                      background: 'var(--p-color-bg-surface)',
                      border: '1px solid var(--p-color-border-subdued)',
                      borderRadius: 12, padding: '2px 6px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      fontSize: 15, cursor: 'pointer', transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--p-color-bg-surface)'; }}
                  >
                    {(() => {
                      const grouped = new Map<string, number>();
                      for (const r of msg.reactions) {
                        grouped.set(r.emoji, (grouped.get(r.emoji) ?? 0) + 1);
                      }
                      return Array.from(grouped.entries()).map(([emoji, count]) => (
                        <span key={emoji} title={`${count} reacción(es)`} style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <TwemojiText size={16}>{emoji}</TwemojiText>
                          {count > 1 ? <span style={{ fontSize: 11, marginLeft: 1 }}>{count}</span> : null}
                        </span>
                      ));
                    })()}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
}
